/**
 * Dekomposition av besparingen.
 * Visar varje komponent som plus eller minus, med totalsumman som
 * sanity-check längst ner.
 */

import type { SavingsBreakdown } from "../sim/costs";

interface DecompositionProps {
  breakdown: SavingsBreakdown;
}

function formatKr(v: number): string {
  const sign = v >= 0 ? "+" : "−";
  const abs = Math.abs(Math.round(v));
  // Använd non-breaking space (U+00A0) mellan tecken, siffror och enhet
  // så hela värdet aldrig radbryts mitt i.
  const formatted = new Intl.NumberFormat("sv-SE")
    .format(abs)
    .replace(/\s/g, "\u00A0");
  return `${sign}\u00A0${formatted}\u00A0kr`;
}

interface Row {
  label: string;
  value: number;
  sign: "positive" | "negative" | "neutral";
  description?: string;
}

export default function DecompositionPanel({ breakdown }: DecompositionProps) {
  const importTotal =
    breakdown.importSpotSavings +
    breakdown.importMarkupSavings +
    breakdown.importTransmissionSavings +
    breakdown.importEnergyTaxSavings +
    breakdown.importVatSavings;

  const exportTotal =
    breakdown.exportSpotLoss +
    breakdown.exportFeedInLoss +
    breakdown.exportCreditLoss +
    breakdown.exportBonusLoss;

  const importRows: Row[] = [
    {
      label: "Spotpris",
      value: breakdown.importSpotSavings,
      sign: "positive",
      description: "Energin du slipper köpa till spot.",
    },
    {
      label: "Elhandlarens påslag",
      value: breakdown.importMarkupSavings,
      sign: "positive",
    },
    {
      label: "Elöverföring",
      value: breakdown.importTransmissionSavings,
      sign: "positive",
      description:
        "Nätbolagets per-kWh-avgift för transport av el. Varierar mellan nätbolag.",
    },
    {
      label: "Energiskatt",
      value: breakdown.importEnergyTaxSavings,
      sign: "positive",
      description: "Lagstadgad 36 öre/kWh per importerad kWh (2026).",
    },
    {
      label: "Moms 25%",
      value: breakdown.importVatSavings,
      sign: "positive",
    },
  ];

  const exportRows: Row[] = [
    {
      label: "Spotpris",
      value: -breakdown.exportSpotLoss,
      sign: "negative",
      description: "Intäkt du tappar genom att inte sälja.",
    },
    {
      label: "Nätnytta",
      value: -breakdown.exportFeedInLoss,
      sign: "negative",
    },
  ];

  if (Math.abs(breakdown.exportCreditLoss) > 1) {
    exportRows.push({
      label: "60-öringen",
      value: -breakdown.exportCreditLoss,
      sign: "negative",
      description: "Skattereduktion som upphör 2025-12-31.",
    });
  }

  if (Math.abs(breakdown.exportBonusLoss) > 1) {
    exportRows.push({
      label: "Elhandlarbonus",
      value: -breakdown.exportBonusLoss,
      sign: "negative",
    });
  }

  const otherRows: Row[] = [];
  if (Math.abs(breakdown.fixedFeeDelta) > 1) {
    otherRows.push({
      label: "Fasta nätavgifter",
      value: breakdown.fixedFeeDelta,
      sign: breakdown.fixedFeeDelta >= 0 ? "positive" : "negative",
      description:
        breakdown.baselineTier !== breakdown.batteryTier
          ? `Tier-byte: baseline ${breakdown.baselineTier === "above" ? "över" : "under"} tröskel, med batteri ${breakdown.batteryTier === "above" ? "över" : "under"}.`
          : undefined,
    });
  }
  if (Math.abs(breakdown.powerTariffDelta) > 1) {
    const isSaving = breakdown.powerTariffDelta >= 0;
    otherRows.push({
      label: isSaving ? "Effekttariff" : "Effekttariff (ökad kostnad)",
      value: breakdown.powerTariffDelta,
      sign: isSaving ? "positive" : "negative",
      description: isSaving
        ? "Sparat genom kapade toppar."
        : "Batteriets laddning skapade högre import-toppar än utan batteri.",
    });
  }
  if (breakdown.ancillaryRevenue > 0) {
    otherRows.push({
      label: "Stödtjänster (FCR-D)",
      value: breakdown.ancillaryRevenue,
      sign: "positive",
    });
  }

  return (
    <section className="decomposition">
      <h3>Var kommer pengarna ifrån?</h3>
      <p className="hint">
        Varje krona av besparingen i sin källa. Plus är vinst (sparat eller
        intjänat), minus är förlust (tappad försäljning eller ökade
        kostnader).
      </p>

      <div className="decomposition-section">
        <header>
          <h4>Vinster: minskad import</h4>
          <span className={`group-total positive`}>
            {formatKr(importTotal)}
          </span>
        </header>
        <ul>
          {importRows.map((r) => (
            <DecompositionRow key={r.label} row={r} />
          ))}
        </ul>
      </div>

      <div className="decomposition-section">
        <header>
          <h4>Förluster: minskad export</h4>
          <span className={`group-total negative`}>
            {formatKr(-exportTotal)}
          </span>
        </header>
        <ul>
          {exportRows.map((r) => (
            <DecompositionRow key={r.label} row={r} />
          ))}
        </ul>
      </div>

      {otherRows.length > 0 && (
        <div className="decomposition-section">
          <header>
            <h4>Övrigt</h4>
          </header>
          <ul>
            {otherRows.map((r) => (
              <DecompositionRow key={r.label} row={r} />
            ))}
          </ul>
        </div>
      )}

      <div className="decomposition-total">
        <span>Total besparing per år</span>
        <strong className={breakdown.totalSavings >= 0 ? "positive" : "negative"}>
          {formatKr(breakdown.totalSavings)}
        </strong>
      </div>
    </section>
  );
}

function DecompositionRow({ row }: { row: Row }) {
  return (
    <li>
      <div className="row-main">
        <span>{row.label}</span>
        <strong className={row.sign}>{formatKr(row.value)}</strong>
      </div>
      {row.description && <small>{row.description}</small>}
    </li>
  );
}
